import React from 'react';
import PropTypes from 'prop-types';
import { IntlShape, defineMessages } from 'react-intl';
import isEqual from 'lodash-es/isEqual';

import MultiSelect, {
  removeItem as removeMultiSelectItem,
  addItem as addMultiSelectItem,
  splitItems as splitMultiSelectItems,
} from '@/shared/components/input/multiselect/MultiSelect';
import intlShape from '@/shared/utils/intlShape';
import CompanyMultiSelect from '@/shared/components/input/multiselect/CompanyMultiSelect';
import LegalEntityMultiSelect from '@/shared/components/input/multiselect/LegalEntityMultiSelect';
import EnumMultiSelect from '@/shared/components/input/multiselect/EnumMultiSelect';
import EndFiscalYearMultiSelect from '@/shared/components/input/multiselect/EndFiscalYearMultiSelect';
import LegalFormMultiSelect from '@/shared/components/input/multiselect/LegalFormMultiSelect';
import TeamMultiSelect from '@/shared/components/input/multiselect/TeamMultiSelect';
import RelationUserSearch from '@/shared/components/input/RelationUserSearch';
import UserSearch from '@/shared/components/input/UserSearch';
import AssignmentTemplateSelector from '@/shared/components/input/AssignmentTemplateSelector';
import { hasMultipleCompanies } from '@/shared/components/security/HasMultiCompany';
import { useHasMultipleLegalEntities } from '@/shared/components/security/HasMultipleLegalEntities';
import CrmTypeButtonGroup from '@/shared/components/CrmTypeButtonGroup';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import { hasAssignments } from '@/shared/components/security/HasAssignments';
import RelationGroupMultiSelect from '@/shared/components/input/multiselect/RelationGroupMultiSelect';
import employeeProfileMessages from '@/shared/messages/employeeProfileMessages';
import DelayedDateRangePicker from '@/shared/components/input/DelayedDateRangePicker';
import AuthService from '@/shared/services/AuthService';
import YesNoSingleSelect from '@/shared/components/input/select/YesNoSingleSelect';
import YearSelect from '@/shared/components/input/YearSelect';

const messages = defineMessages({
  EmptyReceiveInvoiceSoftware: 'Without receive invoice software',
  WithRequiredAuthorizationWarning: 'With required authorization warning',
  WithRelationLabels: 'With relation labels',
  WithoutRelationLabels: 'Without relation labels',
  CrmType: 'Crm type',
  RelationType: 'Relation type',
  LegalForms: 'Legal forms',
  OwnCompany: 'Own company',
  LegalEntity: 'Legal entity',
  AssignmentTemplate: 'Assignment template',
  RelationsAssignedTo: 'Relations assigned to',
  EndFiscalYear: 'End fiscal year',
  AccountingSoftware: 'Accounting software',
  ReceiveInvoiceSoftware: 'Receive invoice software',
  OtherSoftware: 'Other software',
  SendInvoiceSoftware: 'Send invoice software',
  EmptyAccountingSoftware: 'Empty accounting software',
  Teams: 'Teams',
  ReferenceYear: 'Reference year',
  WithoutReferenceYear: 'Without reference year',
  VatPeriodicity: 'Vat periodicity',
  CreatedBy: 'Created by',
  FinancialStatementScheme: 'FinancialStatementScheme',
  SocialSecurityFund: 'SocialSecurityFund',
  SocialSecretariat: 'SocialSecretariat',
  WithTaskLabels: 'With task labels',
  WithoutTaskLabels: 'Without task labels',
  RelationGroups: 'Relation groups',
  ExecutionDate: 'Execution date',
  ExecutionDateFrom: 'Execution from',
  ExecutionDateUntil: 'Execution until',
  MixedTaxPayer: 'Mixed tax payer',
  MixedTaxPayerVatDeductionMethod: 'Mixed tax payer vat deduction method',
  EmptyMixedTaxPayerVatDeductionMethod: 'Without mixed tax payer VAT deduction method',
  Selfbooker: 'Selfbooker',
  RelationHandlingLevel: 'Treatment level',
});

const filterObjects = [
  { id: 'crmType', label: messages.CrmType },
  { id: 'crmTypes', label: messages.CrmType },
  { id: 'relationType', label: messages.RelationType },
  { id: 'relationTypes', label: messages.RelationType },
  { id: 'legalForms', label: messages.LegalForms },
  { id: 'responsible', label: employeeProfileMessages.Responsible },
  { id: 'assistant', label: employeeProfileMessages.Assistant },
  { id: 'associate', label: employeeProfileMessages.Associate },
  { id: 'assistantBackup', label: employeeProfileMessages.AssistantBackup },
  { id: 'billingUser', label: employeeProfileMessages.BillingUser },
  { id: 'seniorUser', label: employeeProfileMessages.SeniorUser },
  { id: 'relationTags', label: messages.WithRelationLabels },
  { id: 'withoutRelationTags', label: messages.WithoutRelationLabels },
  { id: 'relationUserId', label: messages.RelationsAssignedTo },
  { id: 'endFiscalYears', label: messages.EndFiscalYear },
  { id: 'accountingSoftware', label: messages.AccountingSoftware },
  { id: 'receiveInvoiceSoftware', label: messages.ReceiveInvoiceSoftware },
  { id: 'otherSoftware', label: messages.OtherSoftware },
  { id: 'sendInvoiceSoftware', label: messages.SendInvoiceSoftware },
  { id: 'teams', label: messages.Teams },
  { id: 'vatPeriodicity', label: messages.VatPeriodicity },
  { id: 'financialStatementScheme', label: messages.FinancialStatementScheme },
  { id: 'socialSecurityFund', label: messages.SocialSecurityFund },
  { id: 'socialSecretariat', label: messages.SocialSecretariat },
  { id: 'createdBy', label: messages.CreatedBy },
  { id: 'withTags', label: messages.WithTaskLabels },
  { id: 'withoutTags', label: messages.WithoutTaskLabels },
  { id: 'relationGroups', label: messages.RelationGroups },
  {
    id: 'year',
    label: messages.ReferenceYear,
    enabled: () => AuthService.hasFeature('ReferenceYear'),
  },
  {
    id: 'executionDateFrom',
    label: messages.ExecutionDate,
    enabled: () => AuthService.hasFeature('CapacityPlanning'),
  },
  {
    id: 'ownCompanies',
    label: messages.OwnCompany,
    enabled: () => hasMultipleCompanies(),
  },
  {
    id: 'legalEntities',
    label: messages.LegalEntity,
    enabled: () => AuthService.hasFeature('TenantMoore'),
  },
  {
    id: 'assignmentTemplateId',
    label: messages.AssignmentTemplate,
    enabled: () => hasAssignments(),
  },
  {
    id: 'withRequiredAuthorizationWarning',
    label: messages.WithRequiredAuthorizationWarning,
  },
  {
    id: 'mixedTaxPayer',
    label: messages.MixedTaxPayer,
  },
  {
    id: 'mixedTaxPayerVatDeductionMethod',
    label: messages.MixedTaxPayerVatDeductionMethod,
  },
  {
    id: 'selfBooker',
    label: messages.Selfbooker,
  },
  {
    id: 'relationHandlingLevels',
    label: messages.RelationHandlingLevel,
  },
];
const getEnabledFilters = dynamicFilters =>
  dynamicFilters.filter(f => {
    if (typeof f.enabled === 'undefined') return true;
    if (typeof f.enabled === 'function') return !!f.enabled();
    return !!f.enabled;
  });

export const getDynamicFiltersByKey = (keys = []) =>
  filterObjects.filter(x => keys.indexOf(x.id) >= 0);

export const getDynamicFiltersFromDefaultFilters = (defaultFilterObject = {}) => {
  const keys = Object.keys(defaultFilterObject);
  return getDynamicFiltersByKey(keys);
};

export const initDynamicFilters = (
  intl,
  dynamicFilters = [], // Dynamic filters {id,label}
  visibleFilters = [], // Keys from storage
  filterObject = {}, // Filter object from state
  defaultFilterObject = {}, // The default filters
) =>
  getEnabledFilters(dynamicFilters)
    .map(({ id, label }) => ({
      id,
      label: label && label.id ? intl.formatMessage(label) : label?.toString(),
      selected: visibleFilters.includes(id) || !isEqual(filterObject[id], defaultFilterObject[id]),
    }))
    .sort(dynamicSort('label'));

// Use this when selecting a saved filter
export const resetDynamicFilters = (dynamicFilters, filterObject, defaultFilterObject) =>
  dynamicFilters.map(x => ({
    ...x,
    selected: x.selected || !isEqual(filterObject[x.id], defaultFilterObject[x.id]),
  }));

export const handleDynamicFiltersChanged = (
  dynamicFilters,
  item,
  selected,
  filterObject,
  defaultFilterObject,
) =>
  dynamicFilters.map(x => {
    if (item.id === 'selectall') {
      if (!selected && !isEqual(filterObject[x.id], defaultFilterObject[x.id]))
        return { ...x, selected: true }; // Keep used filter on screen
      return { ...x, selected };
    }
    if (x.id === item.id) {
      return { ...x, selected };
    }
    return x;
  });

const DynamicTaskFilter = props => {
  const {
    type,
    filters,
    filterChanged,
    intl,
    labels = [],
    taskLabels = [],
    renderCustomFilter,
    columnClassName,
  } = props;
  const hasMultipleLegalEntities = useHasMultipleLegalEntities();
  switch (type) {
    case 'crmType':
    case 'crmTypes':
      return (
        <div className={columnClassName}>
          <div className="form-group">
            <CrmTypeButtonGroup
              selectedTypes={filters[type]}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
            />
          </div>
        </div>
      );
    case 'executionDateFrom':
      return (
        <div className={columnClassName}>
          <div className="form-group">
            <div className="cx-filter-date-range-with-icon">
              <i
                className="fa fa-calendar-check-o cx-filter-date-range-with-icon__icon"
                title={intl.formatMessage(messages.ExecutionDate)}
                aria-hidden="true"
              />
              <DelayedDateRangePicker
                startDate={filters.executionDateFrom}
                startDateId="TaskOverviewFilterExecutionDateFrom"
                endDate={filters.executionDateUntil}
                endDateId="TaskOverviewFilterExecutionDateUntil"
                onDatesChange={({ startDate, endDate }) =>
                  filterChanged({
                    executionDateFrom: startDate,
                    executionDateUntil: endDate,
                  })
                }
                startDatePlaceholderText={intl.formatMessage(messages.ExecutionDateFrom)}
                endDatePlaceholderText={intl.formatMessage(messages.ExecutionDateUntil)}
                block
              />
            </div>
          </div>
        </div>
      );
    case 'relationType':
    case 'relationTypes':
      return (
        <div className={columnClassName}>
          <div className="form-group">
            <EnumMultiSelect
              type="relationType"
              selection={filters[type]}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
              placeholder={intl.formatMessage(messages.RelationType)}
            />
          </div>
        </div>
      );
    case 'legalForms':
      return (
        <div className={columnClassName}>
          <div className="form-group">
            <LegalFormMultiSelect
              selection={filters.legalForms}
              onChange={selection => filterChanged({ legalForms: selection })}
              showWithoutOption
            />
          </div>
        </div>
      );
    case 'responsible':
      return (
        <div className={columnClassName}>
          <div className="form-group">
            <RelationUserSearch
              onChange={userId => filterChanged({ responsible: userId })}
              value={filters.responsible}
              placeholder={intl.formatMessage(employeeProfileMessages.FilterResponsible)}
            />
          </div>
        </div>
      );
    case 'assistant':
      return (
        <div className={columnClassName}>
          <div className="form-group">
            <RelationUserSearch
              onChange={userId => filterChanged({ assistant: userId })}
              value={filters.assistant}
              placeholder={intl.formatMessage(employeeProfileMessages.FilterAssistant)}
            />
          </div>
        </div>
      );
    case 'associate':
      return (
        <div className={columnClassName}>
          <div className="form-group">
            <RelationUserSearch
              onChange={userId => filterChanged({ associate: userId })}
              value={filters.associate}
              placeholder={intl.formatMessage(employeeProfileMessages.FilterAssociate)}
            />
          </div>
        </div>
      );
    case 'assistantBackup':
      return (
        <div className={columnClassName}>
          <div className="form-group">
            <RelationUserSearch
              onChange={userId => filterChanged({ assistantBackup: userId })}
              value={filters.assistantBackup}
              placeholder={intl.formatMessage(employeeProfileMessages.FilterAssistantBackup)}
            />
          </div>
        </div>
      );
    case 'billingUser':
      return (
        <div className={columnClassName}>
          <RelationUserSearch
            onChange={userId => filterChanged({ billingUser: userId })}
            value={filters.billingUser}
            placeholder={intl.formatMessage(employeeProfileMessages.FilterBillingUser)}
          />
        </div>
      );
    case 'seniorUser':
      return (
        <div className={columnClassName}>
          <RelationUserSearch
            onChange={userId => filterChanged({ seniorUser: userId })}
            value={filters.seniorUser}
            placeholder={intl.formatMessage(employeeProfileMessages.FilterSeniorUser)}
          />
        </div>
      );
    case 'relationUserId': {
      return (
        <div className={columnClassName}>
          <div className="form-group">
            <RelationUserSearch
              onChange={userId => filterChanged({ relationUserId: userId })}
              value={filters.relationUserId}
              placeholder={intl.formatMessage(messages.RelationsAssignedTo)}
            />
          </div>
        </div>
      );
    }
    case 'relationTags': {
      const [selectedLabels, availableLabels] = splitMultiSelectItems(
        labels,
        filters[type],
        'label',
      );
      return (
        <div className={columnClassName}>
          <div className="form-group">
            <MultiSelect
              selectedItem={selectedLabels}
              onItemRemoved={item =>
                filterChanged({
                  [type]: removeMultiSelectItem(filters[type], item, 'label'),
                })
              }
              onItemSelected={item =>
                filterChanged({
                  [type]: addMultiSelectItem(filters[type], item, 'label'),
                })
              }
              items={availableLabels}
              placeholder={intl.formatMessage(messages.WithRelationLabels)}
              withFilterBehaviour
              onFilterBehaviourChanged={filterBehaviour => {
                filterChanged({
                  filterBehaviours: {
                    [type]: filterBehaviour,
                  },
                });
              }}
              filterBehaviour={filters.filterBehaviours?.[type]}
            />
          </div>
        </div>
      );
    }
    case 'withoutRelationTags': {
      const [selectedLabels, availableLabels] = splitMultiSelectItems(
        labels,
        filters[type],
        'label',
      );

      return (
        <div className={columnClassName}>
          <div className="form-group">
            <MultiSelect
              selectedItem={selectedLabels}
              onItemRemoved={item =>
                filterChanged({
                  [type]: removeMultiSelectItem(filters[type], item, 'label'),
                })
              }
              onItemSelected={item =>
                filterChanged({
                  [type]: addMultiSelectItem(filters[type], item, 'label'),
                })
              }
              items={availableLabels}
              placeholder={intl.formatMessage(messages.WithoutRelationLabels)}
            />
          </div>
        </div>
      );
    }
    case 'ownCompanies': {
      return hasMultipleCompanies() ? (
        <div className={columnClassName}>
          <div className="form-group">
            <CompanyMultiSelect
              selection={filters.ownCompanies}
              onChange={companies => filterChanged({ ownCompanies: companies })}
            />
          </div>
        </div>
      ) : null;
    }
    case 'legalEntities': {
      return hasMultipleLegalEntities ? (
        <div className={columnClassName}>
          <div className="form-group">
            <LegalEntityMultiSelect
              selection={filters.legalEntities || []}
              onChange={legalEntities => filterChanged({ legalEntities })}
            />
          </div>
        </div>
      ) : null;
    }
    case 'assignmentTemplateId': {
      return hasAssignments() ? (
        <div className={columnClassName}>
          <div className="form-group">
            <AssignmentTemplateSelector
              value={filters.assignmentTemplateId}
              onChange={id => filterChanged({ assignmentTemplateId: id || null })}
              showDefaultAssignment
            />
          </div>
        </div>
      ) : null;
    }
    case 'endFiscalYears': {
      return (
        <div className={columnClassName}>
          <div className="form-group">
            <EndFiscalYearMultiSelect
              selection={filters.endFiscalYears}
              onChange={endFiscalYears => filterChanged({ endFiscalYears })}
            />
          </div>
        </div>
      );
    }
    case 'accountingSoftware': {
      return (
        <div className={columnClassName}>
          <div className="form-group">
            <EnumMultiSelect
              type="accountingSoftware"
              selection={filters[type]}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
              customItems={[
                {
                  id: -1,
                  label: intl.formatMessage(messages.EmptyAccountingSoftware),
                },
              ]}
              placeholder={intl.formatMessage(messages.AccountingSoftware)}
            />
          </div>
        </div>
      );
    }
    case 'receiveInvoiceSoftware': {
      return (
        <div className={columnClassName}>
          <div className="form-group">
            <EnumMultiSelect
              type="receiveInvoiceSoftware"
              selection={filters[type]}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
              customItems={[
                {
                  id: -1,
                  label: intl.formatMessage(messages.EmptyReceiveInvoiceSoftware),
                },
              ]}
              placeholder={intl.formatMessage(messages.ReceiveInvoiceSoftware)}
            />
          </div>
        </div>
      );
    }
    case 'otherSoftware': {
      return (
        <div className={columnClassName}>
          <div className="form-group">
            <EnumMultiSelect
              type="otherSoftware"
              selection={filters[type]}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
              placeholder={intl.formatMessage(messages.OtherSoftware)}
            />
          </div>
        </div>
      );
    }
    case 'sendInvoiceSoftware': {
      return (
        <div className={columnClassName}>
          <div className="form-group">
            <EnumMultiSelect
              type="sendInvoiceSoftware"
              selection={filters[type]}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
              placeholder={intl.formatMessage(messages.SendInvoiceSoftware)}
            />
          </div>
        </div>
      );
    }
    case 'vatPeriodicity': {
      return (
        <div className={columnClassName}>
          <div className="form-group">
            <EnumMultiSelect
              type="vatPeriodicity"
              selection={filters[type]}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
              placeholder={intl.formatMessage(messages.VatPeriodicity)}
            />
          </div>
        </div>
      );
    }
    case 'financialStatementScheme': {
      return (
        <div className={columnClassName}>
          <div className="form-group">
            <EnumMultiSelect
              type="financialStatementScheme"
              selection={filters[type]}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
              placeholder={intl.formatMessage(messages.FinancialStatementScheme)}
            />
          </div>
        </div>
      );
    }
    case 'socialSecurityFund': {
      return (
        <div className={columnClassName}>
          <div className="form-group">
            <EnumMultiSelect
              type="socialSecurityFund"
              selection={filters[type]}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
              placeholder={intl.formatMessage(messages.SocialSecurityFund)}
            />
          </div>
        </div>
      );
    }
    case 'socialSecretariat': {
      return (
        <div className={columnClassName}>
          <div className="form-group">
            <EnumMultiSelect
              type="socialSecretariat"
              selection={filters[type]}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
              placeholder={intl.formatMessage(messages.SocialSecretariat)}
            />
          </div>
        </div>
      );
    }
    case 'teams': {
      return (
        <div className={columnClassName}>
          <div className="form-group">
            <TeamMultiSelect
              selection={filters.teams}
              onChange={teams => filterChanged({ teams })}
              idProp="code"
            />
          </div>
        </div>
      );
    }
    case 'year': {
      return (
        <div className={columnClassName}>
          <div className="form-group">
            <YearSelect
              value={filters[type]}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
              placeholder={intl.formatMessage(messages.ReferenceYear)}
              withoutOption={intl.formatMessage(messages.WithoutReferenceYear)}
            />
          </div>
        </div>
      );
    }
    case 'createdBy':
      return (
        <div className={columnClassName}>
          <div className="form-group">
            <UserSearch
              onChange={user => filterChanged({ createdBy: user ? user.id : null })}
              value={filters.createdBy}
              placeholder={intl.formatMessage(messages.CreatedBy)}
              withByPlanning
            />
          </div>
        </div>
      );
    case 'withTags': {
      const [selectedLabels, availableLabels] = splitMultiSelectItems(
        taskLabels,
        filters[type],
        'id',
      );

      return (
        <div className={columnClassName}>
          <div className="form-group">
            <MultiSelect
              selectedItem={selectedLabels}
              onItemRemoved={item =>
                filterChanged({
                  [type]: removeMultiSelectItem(filters[type], item, 'id'),
                })
              }
              onItemSelected={item =>
                filterChanged({
                  [type]: addMultiSelectItem(filters[type], item, 'id'),
                })
              }
              items={availableLabels}
              placeholder={intl.formatMessage(messages.WithTaskLabels)}
              withFilterBehaviour
              onFilterBehaviourChanged={filterBehaviour => {
                filterChanged({
                  filterBehaviours: {
                    [type]: filterBehaviour,
                  },
                });
              }}
              filterBehaviour={filters.filterBehaviours?.[type]}
            />
          </div>
        </div>
      );
    }
    case 'withoutTags': {
      const [selectedLabels, availableLabels] = splitMultiSelectItems(
        taskLabels,
        filters[type],
        'id',
      );

      return (
        <div className={columnClassName}>
          <div className="form-group">
            <MultiSelect
              selectedItem={selectedLabels}
              onItemRemoved={item =>
                filterChanged({
                  [type]: removeMultiSelectItem(filters[type], item, 'id'),
                })
              }
              onItemSelected={item =>
                filterChanged({
                  [type]: addMultiSelectItem(filters[type], item, 'id'),
                })
              }
              items={availableLabels}
              placeholder={intl.formatMessage(messages.WithoutTaskLabels)}
            />
          </div>
        </div>
      );
    }
    case 'relationGroups':
      return (
        <div className={columnClassName}>
          <div className="form-group">
            <RelationGroupMultiSelect
              selection={filters[type]}
              placeholder={intl.formatMessage(messages.RelationGroups)}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
            />
          </div>
        </div>
      );
    case 'mixedTaxPayer':
      return (
        <div className={columnClassName}>
          <div className="form-group">
            <YesNoSingleSelect
              value={filters[type]}
              onChange={val => filterChanged({ [type]: val })}
              placeholder={intl.formatMessage(messages.MixedTaxPayer)}
            />
          </div>
        </div>
      );
    case 'mixedTaxPayerVatDeductionMethod': {
      return (
        <div className={columnClassName}>
          <div className="form-group">
            <EnumMultiSelect
              type="MixedTaxPayerVatDeductionMethod"
              selection={filters[type]}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
              customItems={[
                {
                  id: -1,
                  label: intl.formatMessage(messages.EmptyMixedTaxPayerVatDeductionMethod),
                },
              ]}
              placeholder={intl.formatMessage(messages.MixedTaxPayerVatDeductionMethod)}
            />
          </div>
        </div>
      );
    }
    case 'relationHandlingLevels': {
      return (
        <div className={columnClassName}>
          <div className="form-group">
            <EnumMultiSelect
              type="relationHandlingLevel"
              selection={filters[type]}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
              placeholder={intl.formatMessage(messages.RelationHandlingLevel)}
            />
          </div>
        </div>
      );
    }
    case 'selfBooker':
      return (
        <div className={columnClassName}>
          <div className="form-group">
            <YesNoSingleSelect
              value={filters[type]}
              onChange={val => filterChanged({ [type]: val })}
              placeholder={intl.formatMessage(messages.Selfbooker)}
            />
          </div>
        </div>
      );
    default:
      return renderCustomFilter(props);
  }
};
DynamicTaskFilter.propTypes = {
  type: PropTypes.string.isRequired,
  filters: PropTypes.shape({}).isRequired,
  filterChanged: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
  labels: PropTypes.arrayOf(PropTypes.shape({})),
  taskLabels: PropTypes.arrayOf(PropTypes.shape({})),
  renderCustomFilter: PropTypes.func,
  columnClassName: PropTypes.string,
};
DynamicTaskFilter.defaultProps = {
  labels: [],
  renderCustomFilter: props => null, // eslint-disable-line
  columnClassName: 'col-lg-4 col-sm-6',
};
export default DynamicTaskFilter;
